/**
 * src/features/auth/ui/PasswordField.tsx
 *
 * WR-03 (43-REVIEW.md) — the PWD-07 password-field idiom (a STATIC show/hide toggle node, an
 * independent role="status" Caps Lock hint, and a context-specific `autoComplete` pairing) was
 * copy-pasted three times: PasswordSignInForm.tsx, a local `PasswordField` inside
 * SecuritySettingsCard.tsx, and ResetPasswordPage.tsx. Any future a11y fix or Caps Lock
 * detection quirk had to be applied in three places by hand, with nothing enforcing that they
 * stayed in sync beyond duplicated assertions in three separate test files.
 *
 * Extracted here as the single shared implementation — a pure extraction, not a redesign.
 * Markup and behavior are unchanged from the three call sites' prior inline copies:
 *   - The show/hide `<button>` stays the SAME DOM node across states (only `type` on the
 *     sibling input, the icon, and the two a11y attributes change) — conditionally rendering
 *     two different buttons would recreate the node, break password managers, and steal focus.
 *   - The Caps Lock hint lives in its own `role="status"` node, independent per field instance
 *     (each PasswordField owns its own `capsLockOn` state) — turning it on for one field must
 *     never render it for a sibling field.
 */
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';

export interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  autoComplete: 'new-password' | 'current-password';
  disabled: boolean;
}

export const PasswordField: FC<PasswordFieldProps> = ({
  value,
  onChange,
  label,
  autoComplete,
  disabled,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  // Shared by onKeyUp and onFocus: onFocus additionally catches the case where the field
  // regains focus while Caps Lock is already on. React's FocusEvent type has no
  // getModifierState (only Keyboard/Mouse/Touch events do — @types/react), so the native
  // event is read through an optional-method cast instead of `any`; browsers that don't
  // expose it on focus simply never flip the hint from that path, which is harmless.
  const handleKeyEvent = (
    e: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
  ) => {
    const native = e.nativeEvent as { getModifierState?: (key: string) => boolean };
    setCapsLockOn(Boolean(native.getModifierState?.('CapsLock')));
  };

  return (
    <label className="flex flex-col gap-1">
      <span className="text-body font-medium">{label}</span>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          onKeyUp={handleKeyEvent}
          onFocus={handleKeyEvent}
          onBlur={() => setCapsLockOn(false)}
          required
          disabled={disabled}
          className="h-11 w-full pr-10 border border-slate-200 dark:border-slate-700 rounded-input px-3 text-body focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 disabled:opacity-50"
        />
        {/* Static node (PWD-07 / roadmap criterion 4) — the SAME <button> stays mounted
            across states — only `type` on the sibling input and the two a11y attributes
            below change. */}
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-pressed={visible}
          aria-label={t(
            visible ? 'auth.password.hide' : 'auth.password.show',
            visible ? 'Hide password' : 'Show password',
          )}
          className="absolute right-0 inset-y-0 w-10 flex items-center justify-center text-slate-500 dark:text-slate-400"
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {/* Own role="status" node — never shares a slot with a form's role="alert" error. */}
      {capsLockOn && (
        <p role="status" aria-live="polite" className="text-body text-amber-600 dark:text-amber-400">
          {t('auth.password.capslock_hint', 'Caps Lock is on')}
        </p>
      )}
    </label>
  );
};
